/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgModel,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import {
  AfterViewInit, Component, ElementRef, forwardRef, Input, OnInit, Renderer2, ViewChild,
} from '@angular/core';
import { TranslationService } from '../../common/translation.service';

/**
 * Component for our aero calendar picker.
 */
@Component({
  selector: 'aero-calendar-picker',
  templateUrl: './aero-calendar-picker.component.html',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AeroCalendarPickerComponent),
    multi: true,
  },
  {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => AeroCalendarPickerComponent),
    multi: true,
  }],
  standalone: false,
})
export class AeroCalendarPickerComponent implements ControlValueAccessor, AfterViewInit, OnInit, Validator {
  /**
   * Set the field is required.
   */
  @Input() required : boolean = false;

  /**
   * Set the placeholder for the field.
   */
  @Input() placeholder : string = '';

  /**
   * set the field is disabled.
   */
  @Input() disabled : boolean = false;

  /**
   * set the label for the field.
   */
  @Input() label : string = '';

  /**
   * Set the error message for the field.
   */
  @Input() errorMessage : string = '';

  /**
   * Set the hint message for the field with default values as MM/DD/YYYY.
   */
  @Input() hintMessage : string = 'MM/DD/YYYY';

  /**
   * Set the value as true for showing the calendar icon else false.
   */
  @Input() showIcon : boolean = true;

  /**
   * Set the icon for the calendar
   */
  @Input() calendarIcon: string = 'far fa-calendar-alt';

  /**
   * Set the picker view date or month.
   * Default value is date picker.
   */
  @Input() pickerView : any = 'date';

  /**
   * Set the id for the calendar.
   */
  @Input() inputId : string;

  /**
   * Set the value as false for user to type the date in the input
   */
  @Input() readonlyInput : boolean = false;

  /**
   * Set the date format for the field.
   * Default value is mm/dd/yy
   */
  @Input() dateFormat : string = 'mm/dd/yy';

  /**
   * To show the year dropdown on the calendar picker.
   */
  @Input() yearNavigator : boolean = true;

  /**
   * To show the month dropdown on the calandar picker.
   */
  @Input() monthNavigator : boolean = true;

  /**
   * To set the range for the year navigator.
   */
  @Input() yearRange : string;

  /**
   * User can set the minimum date.
   */
  @Input() minDate : Date;

  /**
   * User can set the maximum date.
   */
  @Input() maxDate : Date;

  /**
   * Set the html name attribute for the field.
   */
  @Input() name : string;

  /**
   * Reference for the aero calendar.
   */
  @ViewChild('calendar') aeroCalender: NgModel;

  /**
   * Value for the aero calendar.
   */
  private value : Date;

  /**
   * Translate the aero calendar fields.
   */
  public locale : any;

  /**
   * Default Constructor.
   */
  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private $translate : TranslationService,
  ) {}

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit(): void {
    // Translate the default error message
    this.errorMessage = this.errorMessage || this.$translate.translateInstant('validation_error_required');
    this.locale = this.translateCalendarFields();
  }

  /**
   * A lifecycle hook that is called after view initialized.
   */
  ngAfterViewInit() {
    // set the aria-label for icon button and translate it.
    if (this.elementRef && this.elementRef.nativeElement.querySelector('button')) {
      this.renderer.setAttribute(
        this.elementRef.nativeElement.querySelector('button'),
        'aria-label',
        this.$translate.translateInstant('aero_calender_icon'),
      );
    }
  }

  /**
   * Get the value of the input.
   */
  get inputValue(): any {
    return this.value;
  }

  /**
   * Set the value of the input.
   *
   * @param newValue of the input field.
   */
  set inputValue(newValue: any) {
    if (this.value !== newValue) {
      this.value = newValue;
      this.onChange(newValue);
      this.onValidationChange();
    }
  }

  /**
   * Callback function to register the UI value changes.
   */
  onChange: any = () => { };

  /**
   * Callback function to register on element touch.
   */
  onTouched: any = () => { };

  /**
   * Callback function for validation change.
   */
  onValidationChange: any = () => {};

  /**
   * Set the touched on the blur event.
   */
  onBlur() {
    this.onTouched();
  }

  /**
   * Method to set the value.
   *
   * @param updateValue of the input field.
   */
  writeValue(updateValue: any) {
    if (updateValue !== this.value) {
      this.value = updateValue;
    }
  }

  /**
   * Method gets triggered when the UI element value changes.
   *
   * @param fn callback function.
   */
  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  /**
   * Method gets triggered when the UI element on touched.
   *
   * @param fn callback function.
   */
  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  /**
   * Validate the custom error for the aero calendar.
   *
   * @param control of the input field.
   * @return validation error/null
   */
  validate(control: AbstractControl): ValidationErrors {
    if (control && !control.pristine && this.elementRef.nativeElement.querySelector('input').value) {
      return this.validateDate();
    }
    return null;
  }

  /**
   * Method gets triggered on the validation changes.
   *
   * @param fn callback function.
   */
  registerOnValidatorChange(fn: any): void {
    this.onValidationChange = fn;
  }

  /**
   * Method that is invoked on an update of a model.
   */
  updateChanges() {
    this.onChange(this.value);
  }

  /**
   * Translate the calendar labels.
   */
  translateCalendarFields(): any {
    const dayNames = ['aero_calendar_day_name_sunday', 'aero_calendar_day_name_monday', 'aero_calendar_day_name_tuesday',
      'aero_calendar_day_name_wednesday', 'aero_calendar_day_name_thursday', 'aero_calendar_day_name_friday',
      'aero_calendar_day_name_saturday'];

    const dayNamesShort = ['aero_calendar_short_day_name_sunday', 'aero_calendar_short_day_name_monday',
      'aero_calendar_short_day_name_tuesday', 'aero_calendar_short_day_name_wednesday',
      'aero_calendar_short_day_name_thursday', 'aero_calendar_short_day_name_friday',
      'aero_calendar_short_day_name_saturday'];

    const dayNamesMin = ['aero_calendar_min_day_name_sunday', 'aero_calendar_min_day_name_monday',
      'aero_calendar_min_day_name_tuesday', 'aero_calendar_min_day_name_wednesday',
      'aero_calendar_min_day_name_thursday', 'aero_calendar_min_day_name_friday',
      'aero_calendar_min_day_name_saturday'];

    const monthNames = ['aero_calendar_month_name_january', 'aero_calendar_month_name_february',
      'aero_calendar_month_name_march', 'aero_calendar_month_name_april', 'aero_calendar_month_name_may',
      'aero_calendar_month_name_june', 'aero_calendar_month_name_july', 'aero_calendar_month_name_august',
      'aero_calendar_month_name_september', 'aero_calendar_month_name_october', 'aero_calendar_month_name_november',
      'aero_calendar_month_name_december'];

    const monthNamesShort = ['aero_calendar_short_month_name_january', 'aero_calendar_short_month_name_february',
      'aero_calendar_short_month_name_march', 'aero_calendar_short_month_name_april', 'aero_calendar_short_month_name_may',
      'aero_calendar_short_month_name_june', 'aero_calendar_short_month_name_july', 'aero_calendar_short_month_name_august',
      'aero_calendar_short_month_name_september', 'aero_calendar_short_month_name_october', 'aero_calendar_short_month_name_november',
      'aero_calendar_short_month_name_december'];

    return {
      firstDayOfWeek: 0,
      dayNames: this.$translate.translateArrayInstant(dayNames),
      dayNamesShort: this.$translate.translateArrayInstant(dayNamesShort),
      dayNamesMin: this.$translate.translateArrayInstant(dayNamesMin),
      monthNames: this.$translate.translateArrayInstant(monthNames),
      monthNamesShort: this.$translate.translateArrayInstant(monthNamesShort),
    };
  }

  /**
   * Validate the date value.
   */
  validateDate() {
    if (!(this.value instanceof Date)) {
      this.aeroCalender.control.setErrors({ invalidDate: true });
      return { invalidDate: true };
    } if (this.minDate && this.value < this.minDate) {
      this.aeroCalender.control.setErrors({ invalidDate: true });
      return { invalidDate: true };
    } if (this.maxDate && this.value > this.maxDate) {
      this.aeroCalender.control.setErrors({ invalidDate: true });
      return { invalidDate: true };
    }
    return null;
  }

  /**
   * Method called by the parent component to reset this field.
   */
  resetField() {
    const fieldControl = this.aeroCalender.control;
    fieldControl.setErrors(null);
    fieldControl.markAsPristine();
    fieldControl.markAsUntouched();
  }
}
